import type { CRMUser } from '../types';
import { canGrantTeamChat, canUseTeamChat } from './userRoles';

export const TEAM_ROOM_ID = 'team';
export const TEAM_CHAT_MEMBER_CAP = 8;
export const TEAM_CHAT_BODY_MAX = 4000;
/** Consecutive messages from the same person fold together inside this window. */
export const TEAM_CHAT_GROUP_GAP_MS = 5 * 60 * 1000;

export type TeamChatKind = 'team' | 'dm' | 'group';

export interface TeamChatMemberRef {
  uid: string;
  displayName: string;
}

export { canGrantTeamChat, canUseTeamChat };

/** Stable id so the same two people always reopen one direct message. */
export function directMessageId(a: string, b: string): string {
  const [x, y] = [a.trim(), b.trim()].sort();
  return `dm_${x}_${y}`;
}

export function channelLabel(kind: TeamChatKind, title: string): string {
  if (kind === 'dm') return title.trim() || 'Direct message';
  const name = (kind === 'team' ? 'team' : title).trim() || 'channel';
  return `#${name.replace(/^#/, '')}`;
}

/**
 * People named with @Display Name in a Team-room message.
 * Longer names win so "@Matthew Lynch" does not also match "@Matthew".
 */
export function mentionedMemberIds(
  body: string,
  members: TeamChatMemberRef[],
  authorId: string,
): string[] {
  let scan = ` ${body.toLowerCase()} `;
  const names = members
    .filter((m) => m.uid && m.uid !== authorId && m.displayName.trim())
    .sort((a, b) => b.displayName.trim().length - a.displayName.trim().length);
  const found: string[] = [];
  for (const member of names) {
    const needle = `@${member.displayName.trim().toLowerCase()}`;
    if (!scan.includes(needle) || found.includes(member.uid)) continue;
    found.push(member.uid);
    scan = scan.split(needle).join(' '.repeat(needle.length));
  }
  return found;
}

/** Who gets a bell. The Team room only bells people who were @named. */
export function bellRecipientIds(input: {
  kind: TeamChatKind;
  memberIds: string[];
  authorId: string;
  body: string;
  members: TeamChatMemberRef[];
}): string[] {
  if (input.kind === 'team') {
    return mentionedMemberIds(input.body, input.members, input.authorId);
  }
  return input.memberIds.filter((id) => id && id !== input.authorId);
}

export function conversationTitle(
  kind: TeamChatKind,
  title: string,
  memberIds: string[],
  selfId: string,
  nameOf: (uid: string) => string,
): string {
  if (kind === 'team') return 'team';
  if (kind === 'group') return title.trim() || 'group';
  const other = memberIds.find((id) => id !== selfId);
  return other ? nameOf(other) : 'Direct message';
}

export function previewText(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length > 140 ? `${flat.slice(0, 137)}…` : flat;
}

export function eligibleChatUsers(users: CRMUser[]): CRMUser[] {
  return users
    .filter((u) => canUseTeamChat(u))
    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
}
